import { $httpChangiRecommends } from '@/core/api';
const DEFAULT_PER_PAGE_DESKTOP = 12;
const DEFAULT_PER_PAGE_MOBILE_TABLET = 12;
const MOBILE_TABLET_MQ = '(max-width: 1024px)';
const getDefaultPerPage = () => {
    // SSR-safe fallback
    if (typeof window === 'undefined')
        return DEFAULT_PER_PAGE_DESKTOP;
    return window.matchMedia(MOBILE_TABLET_MQ).matches
        ? DEFAULT_PER_PAGE_MOBILE_TABLET
        : DEFAULT_PER_PAGE_DESKTOP;
};
import { createPagination } from '@/model';
import { createQueryParamsString, } from '../categories/categories-utils';
import { createBrandsQueryParamsString, createCategoriesQueryParamsString, mapSelectedValues, createUuidsArray, mapSearchCategories, arrayToQueryString, } from './searchUtils';
const state = {
    products: [],
    loaded: false,
    loading: false,
    currentPage: 1,
    perPage: getDefaultPerPage(),
    total: 0,
    totalPages: 0,
    query: '',
    brands: [],
    searchBrands: [],
    brandsLoaded: false,
    recentSearches: [],
    categories: [],
    searchCategories: [],
    priceRanges: null,
    priceRangeFilter: [],
    priceRangeFixed: false,
    currentFilters: [],
    sortBy: null,
    sortOrder: null,
    errorMessage: null,
    productTypesFilter: [],
    selectedProductTypesFilter: [],
};
const mutations = {
    setQuery(state, query) {
        if (state.query !== query) {
            state.currentPage = 1;
        }
        state.query = query;
        if (!state.recentSearches.includes(query)) {
            state.recentSearches = [query, ...state.recentSearches].splice(0, 4);
        }
    },
    clearRecentSearches(state) {
        state.recentSearches = [];
    },
    setProducts(state, products) {
        state.products = products;
    },
    setProductsLoaded(state) {
        state.loaded = true;
    },
    setProductsLoading(state, loading) {
        state.loading = loading;
    },
    setPagination(state, pagination) {
        const { currentPage, perPage, total, totalPages } = pagination;
        state.currentPage = currentPage;
        state.total = total;
        state.totalPages = totalPages;
        if (perPage) {
            state.perPage = perPage;
        }
    },
    setDefaultPagination(state) {
        state.currentPage = 1;
        state.perPage = getDefaultPerPage(); // <-- was DEFAULT_PER_PAGE
        state.total = 0;
        state.totalPages = 0;
    },
    setProductsPerPage(state, perPage) {
        state.perPage = perPage;
    },
    setPage(state, page) {
        state.currentPage = page;
    },
    setBrands(state, brands) {
        state.brands = brands;
    },
    setSearchBrands(state, brands) {
        state.searchBrands = brands;
    },
    setBrandsLoaded(state) {
        state.brandsLoaded = true;
    },
    setCategories(state, categories) {
        state.categories = categories;
    },
    setSearchCategories(state, categories) {
        state.searchCategories = categories;
    },
    setPriceRange(state, priceRanges) {
        state.priceRanges = priceRanges;
    },
    setPriceRangeFilter(state, priceRange) {
        state.priceRangeFilter = priceRange;
    },
    setPriceRangeFixed(state, priceRangeFixed) {
        state.priceRangeFixed = priceRangeFixed;
    },
    setFilters(state, filters) {
        state.currentFilters = filters;
    },
    setSorting(state, sorting) {
        if (typeof sorting.attribute !== 'undefined') {
            state.sortBy = sorting.attribute;
            state.sortOrder = sorting.order;
        }
        else {
            state.sortBy = null;
            state.sortOrder = null;
        }
    },
    setErrorMessage(state, message) {
        state.errorMessage = message;
    },
    setProductTypesFilter(state, productTypes) {
        state.productTypesFilter = productTypes;
    },
    setSelectedProductTypesFilter(state, selectedProductTypes) {
        state.selectedProductTypesFilter = selectedProductTypes;
    },
};
const getters = {};
const actions = {
    getProducts({ commit, state }) {
        commit('setErrorMessage', '');
        commit('setProductsLoading', true);
        let priceParams;
        let sortingParams = [];
        let productTypeParams = {};
        if (state.priceRangeFilter &&
            state.priceRangeFilter[0] !== state.priceRangeFilter[1]) {
            priceParams = JSON.stringify([
                state.priceRangeFilter[0],
                state.priceRangeFilter[1],
            ]);
        }
        if (state.selectedProductTypesFilter.length) {
            productTypeParams.product_sub_type = state.selectedProductTypesFilter;
        }
        let brandParams = createUuidsArray(state.searchBrands);
        brandParams = JSON.stringify(brandParams);
        let categoryParams = createUuidsArray(state.searchCategories);
        categoryParams = JSON.stringify(categoryParams);
        if (state.sortBy) {
            sortingParams = JSON.stringify([state.sortBy, state.sortOrder]);
        }
        $httpChangiRecommends.api
            .ChangiRecommendsProductSearch({
            per_page: state.perPage,
            page: state.currentPage,
            query: state.query,
            ...productTypeParams,
            pricesRange: priceParams,
            brandsUuids: brandParams,
            categoriesUuids: categoryParams,
            sortBy: sortingParams,
        })
            .then(({ data }) => {
            const { current_page, total, per_page, total_pages, } = data.meta.pagination;
            const pagination = createPagination(+current_page, +total, +per_page, +total_pages);
            let categoriesSortedAlphabetically = data.meta.categories.sort((a, b) => a.title > b.title ? 1 : b.title > a.title ? -1 : 0);
            categoriesSortedAlphabetically = categoriesSortedAlphabetically.map((el) => {
                let sortedChildrenCategories = el.children.sort((a, b) => a.title > b.title ? 1 : b.title > a.title ? -1 : 0);
                sortedChildrenCategories = sortedChildrenCategories.map((item) => {
                    const sortedChildrenSubCategories = item.children.sort((a, b) => a.title > b.title ? 1 : b.title > a.title ? -1 : 0);
                    return { ...item, sortedChildrenSubCategories };
                });
                return { ...el, sortedChildrenCategories };
            });
            if (!state.categories.length) {
                commit('setCategories', categoriesSortedAlphabetically);
            }
            if (!state.searchCategories.length) {
                commit('setSearchCategories', categoriesSortedAlphabetically);
            }
            if (!state.searchBrands.length || !JSON.parse(brandParams).length) {
                commit('setBrands', data.meta.brands);
                commit('setSearchBrands', data.meta.brands);
            }
            if (!state.priceRangeFixed) {
                commit('setPriceRange', data.meta.pricesRange);
            }
            commit('setProductTypesFilter', data.meta.product_types);
            commit('setPriceRangeFixed', false);
            for (let i = 0; i < state.searchCategories.length; i++) {
                if (state.searchCategories[i].selected) {
                    commit('setFilters', state.searchCategories[i].attributes);
                    break;
                }
            }
            commit('categoriesModule/setFilterBrands', data.meta.brands, { root: true });
            commit('categoriesModule/setBrandSlug', null, { root: true });
            commit('setProducts', data.data);
            commit('setPagination', pagination);
        })
            .catch((data) => {
            console.log(JSON.stringify(data));
            if (data && data.message) {
                if (data.message.length > 0 && typeof data.message == 'string' && data.message != 'Request failed with status code 404') {
                    commit('setErrorMessage', data.message);
                }
                else {
                    commit('setProducts', []);
                }
            }
            else {
                commit('setErrorMessage', 'Something went wrong! Please reload the page and try again.');
            }
        })
            .finally(() => {
            commit('setProductsLoading', false);
            commit('setProductsLoaded');
            commit('setBrandsLoaded');
        });
    },
    loadInitialData({ state, commit, dispatch }, payload) {
        const { query } = payload;
        commit('setQuery', query);
        commit('setProductsLoading', true);
        const brandPayload = payload.routeQuery.brandUuids;
        const categoryPayload = payload.routeQuery.categoriesUuids;
        if (payload.routeQuery.minPrice && payload.routeQuery.maxPrice) {
            commit('setPriceRangeFilter', [
                payload.routeQuery.minPrice,
                payload.routeQuery.maxPrice,
            ]);
        }
        else {
            commit('setPriceRangeFilter', []);
        }
        if (payload.routeQuery.product_sub_type) {
            commit('setSelectedProductTypesFilter', Array.isArray(payload.routeQuery.product_sub_type) ? payload.routeQuery.product_sub_type : [payload.routeQuery.product_sub_type]);
        }
        else {
            commit('setSelectedProductTypesFilter', []);
        }
        const routeQueryBrands = brandPayload
            ? Array.isArray(brandPayload)
                ? brandPayload
                : [brandPayload]
            : [];
        const routeQueryCategories = categoryPayload
            ? Array.isArray(categoryPayload)
                ? categoryPayload
                : [categoryPayload]
            : [];
        const mappedBrands = mapSelectedValues(payload.brands, routeQueryBrands);
        commit('setSearchBrands', mappedBrands);
        const mappedCategories = mapSelectedValues(payload.categories, routeQueryCategories);
        commit('setSearchCategories', mappedCategories);
        commit('setFilters', []);
        if (payload.routeQuery && payload.routeQuery.date) {
            commit('setSorting', {
                attribute: 'date',
                order: payload.routeQuery.date,
            });
        }
        if (payload.routeQuery && payload.routeQuery.price) {
            commit('setSorting', {
                attribute: 'price',
                order: payload.routeQuery.price,
            });
        }
        if (payload.routeQuery && payload.routeQuery.name) {
            commit('setSorting', {
                attribute: 'name',
                order: payload.routeQuery.name,
            });
        }
        if (!payload.routeQuery || (payload.routeQuery && !(payload.routeQuery.name || payload.routeQuery.price || payload.routeQuery.date))) {
            commit('setSorting', {});
        }
        if (payload.routeQuery && payload.routeQuery.page) {
            commit('setPage', +payload.routeQuery.page);
        }
        else {
            commit('setPage', 1);
        }
        dispatch('getProducts');
    },
    changeSortOrder({ commit, dispatch, state }, sorting) {
        commit('setSorting', sorting);
        // const queryParams = createQueryParamsString(state.currentFilters);
        // const brandsQuery = createBrandsQueryParamsString(state.searchBrands);
        // const categoriesQuery = createCategoriesQueryParamsString(
        //   state.searchCategories,
        // );
        // const newUrl = `${window.location.origin}/search/${
        //   state.query
        // }${queryParams}${queryParams ? '&' : '?'}page=${state.currentPage}${
        //   categoriesQuery ? '&' + categoriesQuery : ''
        // }${brandsQuery ? '&' + brandsQuery : ''}${
        //   state.priceRangeFilter[0] ? '&minPrice=' + state.priceRangeFilter[0] : ''
        // }${
        //   state.priceRangeFilter[1] ? '&maxPrice=' + state.priceRangeFilter[1] : ''
        // }${state.sortBy ? '&' + state.sortBy + '=' + state.sortOrder : ''}`;
        // window.history.pushState({}, '', newUrl);
        // dispatch('getProducts');
        dispatch('loadNewUrl');
    },
    changeProductsView({ commit, dispatch, state }, perPage) {
        commit('setProductsPerPage', perPage);
        commit('setPage', 1);
        // const queryParams = createQueryParamsString(state.currentFilters);
        // const newUrl = `${window.location.origin}${
        //   window.location.pathname
        // }${queryParams}${queryParams ? '&' : '?'}page=1`;
        // window.history.pushState({}, '', newUrl);
        // dispatch('getProducts');
        dispatch('loadNewUrl');
    },
    changePage({ commit, dispatch, state }, page) {
        commit('setPage', page);
        // const brandsQuery = createBrandsQueryParamsString(state.searchBrands);
        // const categoriesQuery = createCategoriesQueryParamsString(
        //   state.searchCategories,
        // );
        // const newUrl = `${window.location.origin}/search/${
        //   state.query
        // }?page=${page}${categoriesQuery ? '&' + categoriesQuery : ''}${
        //   brandsQuery ? '&' + brandsQuery : ''
        // }${
        //   state.priceRangeFilter[0] ? '&minPrice=' + state.priceRangeFilter[0] : ''
        // }${
        //   state.priceRangeFilter[1] ? '&maxPrice=' + state.priceRangeFilter[1] : ''
        // }`;
        // window.history.pushState({}, '', newUrl);
        // dispatch('getProducts');
        dispatch('loadNewUrl');
    },
    changeBrands({ state, commit, dispatch }, brands) {
        const query = state.query;
        if (!query) {
            return;
        }
        commit('setSearchBrands', brands);
        // const brandsQuery = createBrandsQueryParamsString(state.searchBrands);
        // const categoriesQuery = createCategoriesQueryParamsString(
        //   state.searchCategories,
        // );
        // const newUrl = `${window.location.origin}/search/${state.query}?page=1${
        //   categoriesQuery ? '&' + categoriesQuery : ''
        // }${brandsQuery ? '&' + brandsQuery : ''}${
        //   state.priceRangeFilter[0] ? '&minPrice=' + state.priceRangeFilter[0] : ''
        // }${
        //   state.priceRangeFilter[1] ? '&maxPrice=' + state.priceRangeFilter[1] : ''
        // }`;
        commit('setPage', 1);
        // window.history.pushState({}, '', newUrl);
        // dispatch('getProducts');
        dispatch('loadNewUrl');
    },
    changePriceFilter({ state, dispatch, commit, }) {
        // const brandsQuery = createBrandsQueryParamsString(state.searchBrands);
        // const categoriesQuery = createCategoriesQueryParamsString(
        //   state.searchCategories,
        // );
        // const newUrl = `${window.location.origin}/search/${state.query}?page=1${
        //   categoriesQuery ? '&' + categoriesQuery : ''
        // }${brandsQuery ? '&' + brandsQuery : ''}${
        //   state.priceRangeFilter[0] ? '&minPrice=' + state.priceRangeFilter[0] : ''
        // }${
        //   state.priceRangeFilter[1] ? '&maxPrice=' + state.priceRangeFilter[1] : ''
        // }`;
        commit('setPage', 1);
        // window.history.pushState({}, '', newUrl);
        // dispatch('getProducts');
        dispatch('loadNewUrl');
    },
    changeCategory({ state, commit, dispatch }, selectedCategory) {
        const mappedCategories = mapSearchCategories(state.searchCategories, selectedCategory);
        const availableAttributes = selectedCategory.attributes;
        commit('setSearchBrands', []);
        commit('setSearchCategories', mappedCategories);
        commit('setFilters', availableAttributes);
        // const categoriesQuery = createCategoriesQueryParamsString(
        //   state.searchCategories,
        // );
        // const newUrl = `${window.location.origin}/search/${state.query}?page=1${
        //   categoriesQuery ? '&' + categoriesQuery : ''
        // }${
        //   state.priceRangeFilter[0] ? '&minPrice=' + state.priceRangeFilter[0] : ''
        // }${
        //   state.priceRangeFilter[1] ? '&maxPrice=' + state.priceRangeFilter[1] : ''
        // }`;
        commit('setPage', 1);
        // window.history.pushState({}, '', newUrl);
        // dispatch('getProducts');
        dispatch('loadNewUrl');
    },
    applyProductTypesFilter({ state, commit, dispatch }) {
        commit('setPage', 1);
        dispatch('loadNewUrl');
    },
    loadNewUrl({ state, commit, dispatch }) {
        const productTypesQuery = arrayToQueryString(state.selectedProductTypesFilter, 'product_sub_type');
        const brandsQuery = createBrandsQueryParamsString(state.searchBrands);
        const categoriesQuery = createCategoriesQueryParamsString(state.searchCategories);
        const queryParams = createQueryParamsString(state.currentFilters);
        const pageQuery = `${(state.currentPage && state.currentPage > 1) ? 'page=' + state.currentPage : ''}`;
        const sortQuery = `${state.sortBy ? state.sortBy + '=' + state.sortOrder : ''}`;
        let priceRangeQuery = '';
        if (state.priceRangeFilter &&
            state.priceRangeFilter[0] !== state.priceRangeFilter[1]) {
            priceRangeQuery = `minPrice=${state.priceRangeFilter[0]}&maxPrice=${state.priceRangeFilter[1]}`;
        }
        const baseUrl = `${window.location.origin}/search/${state.query}`;
        const queryParts = [queryParams, brandsQuery, categoriesQuery, productTypesQuery, priceRangeQuery, sortQuery, pageQuery]
            .filter(Boolean)
            .map(part => part.replace(/^\?/, ''));
        const allQuery = queryParts.join('&');
        const newUrl = baseUrl + (allQuery ? '?' + allQuery : '');
        window.history.replaceState({}, '', newUrl);
        dispatch('getProducts');
    }
};
export const searchModule = {
    state,
    getters,
    mutations,
    actions,
    namespaced: true,
};
